import React from "react";
import { useTranslation } from "react-i18next";

// Images & Icons
import home_banner_desktop from "assets/images/home_banner_desktop.png";

// Step screenshots
import Step1Img from "assets/images/pages/checkin/Step_1_GNG.png";
import Step2Img from "assets/images/pages/checkin/Step_2_GNG.png";
import Step3Img from "assets/images/pages/checkin/Step_3_GNG.png";
import Step4Img from "assets/images/pages/checkin/Step_4_GNG.png";
import Step5Img from "assets/images/pages/checkin/Step_5_GNG.png";

import { GOLF_PASS_CLIENT_BASE_URL } from "utils/constants/api";

function CheckInSteps() {
  const { t } = useTranslation();

  const steps = [
    {
      number: 1,
      title: t("step1title"),
      description: t("step1description"),
      image: Step1Img,
      imageAlt: "Golf & Groove Official Invitation Pass screen",
    },
    {
      number: 2,
      title: t("step2title"),
      description: t("step2description"),
      image: Step2Img,
      imageAlt: "Sign in screen with phone number input",
    },
    {
      number: 3,
      title: t("step3title"),
      description: t("step3description"),
      image: Step3Img,
      imageAlt: "OTP verification screen",
    },
    {
      number: 4,
      title: t("step4title"),
      description: t("step4description"),
      image: Step4Img,
      imageAlt: "Home screen with My Pass option",
    },
    {
      number: 5,
      title: t("step5title"),
      description: t("step5description"),
      image: Step5Img,
      imageAlt: "Tournament Pass QR code screen",
    },
  ];

  const handleOpenGolfPass = () => {
    window.open(GOLF_PASS_CLIENT_BASE_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      {/* Banner */}
      <section>
        <div className="relative">
          <img
            src={home_banner_desktop}
            alt="main"
            className="object-cover object-center w-full max-h-40 sm:max-h-60"
          />
          <div className="absolute top-0 bottom-0 left-0 right-0 text-white bg-black/60">
            <div className="flex flex-col items-center justify-center h-full gap-4 app-main-container sm:gap-8">
              <p className="max-w-md px-8 text-lg font-semibold text-center sm:text-3xl sm:font-bold app-animation-translate-start">
                {t("checkinLabelBanner")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section>
        <div className="app-main-container">
          <div className="mb-8 sm:mb-10">
            <h2 className="mb-3 text-xl font-bold text-app-blue md:text-3xl">
              {t("checkinHeaderTitle")}
            </h2>
            <p className="text-sm leading-relaxed text-gray-700 md:text-base">
              {t("checkinHeaderLabel")}
            </p>
          </div>

          {/* Initial step note */}
          <div className="p-4 mb-10 border-l-4 rounded-md bg-emerald-50 border-app-blue sm:p-5 sm:mb-12">
            <div className="text-sm font-bold text-app-blue sm:text-base">
              {t("checkinInitialStepNote1")}
            </div>
            <div className="mt-1 text-sm text-gray-700 sm:text-base">
              {t("checkinInitialStepNote2")}{" "}
              <span className="font-semibold text-app-blue">
                "{t("checkinButtonName")}"
              </span>{" "}
              {t("checkinInitialStepNote3")}
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-10 sm:space-y-14">
            {steps.map((step) => (
              <div
                key={step.number}
                className="flex flex-col items-start gap-6 md:flex-row md:gap-10"
              >
                {/* Step text content */}
                <div className="flex-1 order-2 w-full md:order-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center justify-center w-10 h-10 text-base font-bold text-white rounded-full bg-app-blue sm:w-12 sm:h-12 sm:text-lg">
                      {step.number}
                    </div>
                    <div className="text-xs font-semibold tracking-widest uppercase text-app-blue sm:text-sm">
                      {t("step")} {step.number}
                    </div>
                  </div>
                  <h3 className="mb-3 text-lg font-bold text-app-blue md:text-2xl">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-700 md:text-base">
                    {step.description}
                  </p>
                </div>

                {/* Step screenshot */}
                <div className="flex justify-center order-1 w-full md:w-auto md:order-2 md:justify-end">
                  <div className="overflow-hidden bg-white shadow-lg rounded-2xl ring-1 ring-gray-200">
                    <img
                      src={step.image}
                      alt={step.imageAlt}
                      className="block w-48 h-auto sm:w-56 md:w-60"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="flex flex-col items-center mt-12 sm:mt-16">
            <button
              onClick={handleOpenGolfPass}
              className="inline-flex items-center justify-center px-10 py-3 text-sm font-bold text-white transition-opacity rounded-md shadow-md bg-app-blue hover:opacity-90 sm:text-base sm:px-14 sm:py-4"
            >
              {t("checkinButtonName")}
              <svg
                className="w-4 h-4 ml-2 sm:w-5 sm:h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </button>
            <p className="mt-3 text-xs text-center text-gray-500 sm:text-sm">
              {t("checkinButtonDescription")}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CheckInSteps;
